import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { PageContainer } from "@/layouts/PageContainer";
import { Button } from "@/components/ui/button";
import { RemoveMemberModal } from "@/components/modals/RemoveMemberModal";
import { Users, ChevronLeft, AlertCircle, ShieldCheck, DoorOpen } from "lucide-react";

export default function Members() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const load = useCallback(async (signal) => {
    if (!schoolId) return;
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      if (!res.ok) throw new Error("Failed to load members");
      setMembers(await res.json());
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useAbortableLoader(load);

  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;
    const userId = String(memberToRemove.userId);
    const name = memberToRemove.name;
    setActionInProgress(userId + "remove");
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove member");
      }
      setMembers((prev) => prev.filter((m) => String(m.userId) !== userId));
      toast.success(`${name} removed from school.`);
      setMemberToRemove(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <RemoveMemberModal
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        member={memberToRemove}
        onConfirm={confirmRemoveMember}
        busy={!!actionInProgress}
      />

      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Users size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">Members</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {members.length} teacher{members.length !== 1 ? "s" : ""} in this school
          </p>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20">
          <Users size={28} className="text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No teachers in this school yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {members.map((member) => {
            const uid = String(member.userId);
            const busy = actionInProgress?.startsWith(uid);
            return (
              <div
                key={uid}
                className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl px-5 py-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {member.isAdmin && <ShieldCheck size={16} className="text-primary shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-base font-medium text-foreground truncate">
                      {member.name}
                      {member.isAdmin && (
                        <span className="ml-2 text-sm text-primary font-normal">Admin</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">{member.email}</div>
                  </div>
                </div>

                {!member.isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                    title="Remove member"
                    aria-label="Remove member"
                    disabled={busy}
                    onClick={() => setMemberToRemove(member)}
                  >
                    <DoorOpen size={15} />
                    Remove Member
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
